<template>
  <div class="modal-container flexcol" v-loading.fullscreen.lock="dataLoading">
    <div
      class="modal-header flexrow xbetween ycenter px-3 py-2"
      :class="isEdit ? 'edit-school' : ''"
    >
      <div class="flexrow ycenter fs-s color-white">
        <i class="bi bi-telephone-outbound fs-m me-2"></i>
        <span>{{ isEdit ? "Edit" : "Add" }} School Connect</span>
      </div>
      <ModalCloseButton :color="isEdit ? 'primary' : 'info'" />
    </div>
    <div class="modal-body px-3 py-2">
      <form
        class="user-form"
        @submit.prevent="onFormSubmit"
        v-bind:validation-schema="schoolConnectForm"
      >
        <div v-if="isEdit" class="fs-m fw-bold tight text-start color-primary mb-2">
          {{ cardData.School.Name }}
        </div>
        <div v-else>
          <div class="connect-form-input-grid mb-3">
            <CustomDropdown
              v-if="SchoolOptions"
              inputId="SchoolProfileId"
              inputLabel="School"
              inputPlaceholder="Select School Name"
              :dropdownItems="SchoolOptions"
              :initialValue="schoolConnectForm.values.SchoolProfileId"
              :isClearable="true"
              :isFilterable="true"
              :isMandatory="true"
              :isFormField="true"
            />
          </div>
        </div>
        <div class="connect-form-input-grid mb-3">
          <CustomRadioButton
            inputId="ReasonForConnectId"
            inputLabel="Reason For Connect"
            :radioItems="getIdNamePairsFromEnum(SchoolConnectReasonEnum)"
            :initialValue="schoolConnectForm.values.ReasonForConnectId"
            :isMandatory="true"
            :isFormField="true"
            :isSmall="true"
            :isDisabled="isEdit"
          />
          <CustomRadioButton
            inputId="ConnectStatusId"
            inputLabel="Status"
            :radioItems="getIdNamePairsFromEnum(SchoolConnectStatusEnum)"
            :initialValue="schoolConnectForm.values.ConnectStatusId"
            :isMandatory="true"
            :isFormField="true"
            :isSmall="true"
          />
        </div>
        <el-collapse v-model="activeConnect" :accordion="true" class="mb-3">
          <el-collapse-item
            v-for="(connect, idx) in schoolConnectForm.values.AllConnects"
            :name="String(idx)"
          >
            <template #title>
              <div class="flexrow ycenter xbetween gap-2 w-100">
                <div class="flexrow ycenter gap-2">
                  <div class="serial-number-circle small fs-xxs bg-color-primary dark">
                    {{ idx + 1 }}
                  </div>
                  <div v-if="connect" class="fs-xs fw-bold">
                    <span v-if="connect.CommunicationTypeId" class="color-primary">
                      {{ getNameFromEnumId(connect.CommunicationTypeId, CommunicationTypeEnum) }}
                    </span>
                    <span v-else class="color-danger">{{ "?" }}</span>
                    <i class="bi bi-at mx-1"></i>
                    <span v-if="connect.ContactDate" class="color-primary">
                      {{ getFormattedDate(connect.ContactDate) }}
                    </span>
                    <span v-else class="color-danger">{{ "?" }}</span>
                  </div>
                </div>
                <IconTooltipButton
                  v-if="
                    schoolConnectForm.values.AllConnects &&
                    schoolConnectForm.values.AllConnects.length > 1
                  "
                  iconName="trash"
                  iconColor="danger"
                  iconSize="s"
                  tooltipText="Delete Connect"
                  @onClick="onDeleteConnect(idx)"
                />
              </div>
            </template>
            <div class="connect-details-input-grid">
              <CustomDatePicker
                :inputId="`AllConnects[${idx}].ContactDate`"
                inputLabel="Contact Date"
                :initialValue="connect ? connect.ContactDate : undefined"
                :isMandatory="true"
                :isFormField="true"
                :isSmall="true"
              />
              <CustomRadioButton
                :inputId="`AllConnects[${idx}].CommunicationTypeId`"
                inputLabel="Communication Type"
                :radioItems="getIdNamePairsFromEnum(CommunicationTypeEnum)"
                :initialValue="connect ? connect.CommunicationTypeId : undefined"
                :isMandatory="true"
                :isFormField="true"
                :isSmall="true"
              />
              <CustomTextArea
                :inputId="`AllConnects[${idx}].DetailedNotes`"
                inputLabel="Detailed Notes"
                :initialValue="connect ? connect.DetailedNotes : undefined"
                :numberOfRows="3"
                :isMandatory="true"
                :isFormField="true"
                class="detailed-notes"
              />
            </div>
          </el-collapse-item>
        </el-collapse>
        <div class="flexrow ycenter gap-2 mb-4">
          <div class="button button-outline info-link" @click="onAddConnect">
            <i class="bi bi-plus-circle me-1"></i> Add Follow-Up
          </div>
        </div>
        <div class="flexrow gap-3 my-3">
          <button type="submit" class="button small" :class="isEdit ? `primary` : `info`">
            {{ !isEdit ? "Save Details" : "Update Details" }}
          </button>
          <div v-if="isEdit" class="button small danger-reverse" @click="closeModal">Cancel</div>
          <div v-else class="button small danger-reverse" @click="schoolConnectForm.resetForm()">
            Reset
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import IconTooltipButton from "@/components/IconTooltipButton.vue";
import CustomDatePicker from "@/components/input/CustomDatePicker.vue";
import CustomDropdown from "@/components/input/CustomDropdown.vue";
import CustomRadioButton from "@/components/input/CustomRadioButton.vue";
import CustomTextArea from "@/components/input/CustomTextArea.vue";
import ModalCloseButton from "@/components/modals/ModalCloseButton.vue";
import {
  CommunicationTypeEnum,
  SchoolConnectReasonEnum,
  SchoolConnectStatusEnum,
} from "@/enums/aem";
import {
  getFormattedDate,
  getIdNamePairsFromEnum,
  getNameFromEnumId,
  showCustomMessage,
} from "@/methods/utils";
import { useModalStore } from "@/stores/modalStore";
import { useSchoolStore } from "@/stores/schoolStore";
import { useSessionStore } from "@/stores/sessionStore";
import { SchoolConnectDisplay, SchoolConnectInputs } from "@/types/admin";
import { toTypedSchema } from "@vee-validate/yup";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";
import { defineComponent, onBeforeMount, PropType, ref } from "vue";
import * as yup from "yup";

export default defineComponent({
  name: "ModalSchoolConnectForm",
  components: {
    CustomDatePicker,
    CustomDropdown,
    CustomRadioButton,
    CustomTextArea,
    IconTooltipButton,
    ModalCloseButton,
  },
  props: {
    cardData: { type: Object as PropType<SchoolConnectDisplay>, default: null },
    isEdit: { type: Boolean, default: false },
  },
  emits: ["connectUpdated"],
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const { allApiVerified, UserInfo } = storeToRefs(sessionStore);
    const schoolStore = useSchoolStore();
    const { SchoolOptions, ActiveSchoolProfileId, SchoolCardsReloadKey } = storeToRefs(schoolStore);
    const { getSchoolOptions } = schoolStore;
    const modalStore = useModalStore();
    const { closeModal } = modalStore;
    const activeConnect = ref("0");
    const dataLoading = ref(false);

    onBeforeMount(async () => {
      await getSchoolOptions();
      if (ActiveSchoolProfileId.value) {
        schoolConnectForm.setFieldValue("SchoolProfileId", ActiveSchoolProfileId.value);
      }
    });

    const schoolConnectSchema = toTypedSchema(
      yup.object().shape({
        SchoolProfileId: yup.number().required("*required"),
        MongoId: yup.string().notRequired(),
        OwnerId: yup.number().nullable(),
        ReasonForConnectId: yup.number().required("*required"),
        ConnectStatusId: yup.number().required("*required"),
        AllConnects: yup.array().of(
          yup
            .object()
            .nullable()
            .shape({
              ContactDate: yup.string().required("*required"),
              CommunicationTypeId: yup.number().nullable().required("*required"),
              DetailedNotes: yup.string().required("*required"),
            }),
        ),
      }),
    );
    const schoolConnectForm = useForm({ validationSchema: schoolConnectSchema });

    onBeforeMount(() => {
      schoolConnectForm.setFieldValue("AllConnects", [null]);
      if (props.isEdit && props.cardData) {
        schoolConnectForm.setFieldValue("MongoId", props.cardData.MongoId);
        schoolConnectForm.setFieldValue("SchoolProfileId", props.cardData.School.Id);
        schoolConnectForm.setFieldValue("OwnerId", props.cardData.Owner.Id);
        schoolConnectForm.setFieldValue("ReasonForConnectId", props.cardData.ReasonForConnectId);
        schoolConnectForm.setFieldValue("ConnectStatusId", props.cardData.ConnectStatusId);
        schoolConnectForm.setFieldValue("AllConnects", props.cardData.AllConnects);
      }
    });

    const onFormSubmit = schoolConnectForm.handleSubmit(onValidSubmit, onInvalidSubmit);

    async function onValidSubmit() {
      schoolConnectForm.setFieldValue("OwnerId", UserInfo.value.UserId);
      await saveSchoolConnectForm();
    }

    async function saveSchoolConnectForm() {
      const formData = schoolConnectForm.values as SchoolConnectInputs;
      dataLoading.value = true;
      const response = props.isEdit
        ? await allApiVerified.value.schoolProfileApi.updateSchoolConnect(formData)
        : await allApiVerified.value.schoolProfileApi.saveSchoolConnect(formData);
      dataLoading.value = false;
      if (response.isSuccess) {
        showCustomMessage({
          messageText: "School connect saved successfully.",
          messageType: "success",
        });
        schoolConnectForm.resetForm();
        closeModal();
        SchoolCardsReloadKey.value += 1;
        emit("connectUpdated");
      } else {
        showCustomMessage({ messageText: response.errorMessage, messageType: "error" });
      }
    }

    function onAddConnect() {
      schoolConnectForm.setFieldValue("AllConnects", [
        ...(schoolConnectForm.values.AllConnects || []),
        null,
      ]);
      if (schoolConnectForm.values.AllConnects) {
        activeConnect.value = `${schoolConnectForm.values.AllConnects.length - 1}`;
      }
    }

    function onDeleteConnect(connectIndex: number) {
      const currentConnects = schoolConnectForm.values.AllConnects;
      if (!currentConnects || currentConnects.length <= 1) {
        return;
      }
      // TODO: Check if schoolConnectForm.values.AllConnects can be modified directly
      const updatedConnects = [...currentConnects];
      updatedConnects.splice(connectIndex, 1);
      schoolConnectForm.setFieldValue("AllConnects", updatedConnects);
    }

    function onInvalidSubmit() {
      showCustomMessage({
        messageText: "Oops, there seem to be some errors in the form.",
        messageType: "error",
      });
    }

    return {
      CommunicationTypeEnum,
      SchoolConnectReasonEnum,
      SchoolConnectStatusEnum,
      schoolConnectForm,
      SchoolOptions,
      activeConnect,
      dataLoading,
      onAddConnect,
      onDeleteConnect,
      getIdNamePairsFromEnum,
      onFormSubmit,
      closeModal,
      getNameFromEnumId,
      getFormattedDate,
    };
  },
});
</script>

<style lang="scss" scoped>
.modal-container {
  width: 90%;
  height: 95vh;
  overflow: hidden;
  background-color: $color-white;
  border-radius: 10px;
  @include media("<=s") {
    width: 95%;
  }
  & .modal-header {
    background-color: $color-info;
    &.edit-school {
      background-color: $color-primary-light;
    }
  }
  & .modal-body {
    overflow-y: scroll;
    & .connect-form-input-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      @include media("<=m") {
        grid-template-columns: 1fr;
      }
    }
    & .connect-details-input-grid {
      display: grid;
      grid-template-columns: 180px 360px 1fr;
      column-gap: 10px;
      @include media("<=m") {
        grid-template-columns: 180px 360px;
        & .detailed-notes {
          grid-column: 1 / 3;
        }
      }
      @include media("<=s") {
        grid-template-columns: 1fr;
        row-gap: 10px;
        & .detailed-notes {
          grid-column: 1 / 2;
        }
      }
    }
  }
}
</style>
